<template>
  <teleport to="body">
    <transition
      name="gallery-fade"
      @enter="onEnter"
      @after-enter="onAfterEnter"
      @leave="onLeave"
      @after-leave="onAfterLeave"
    >
      <div
        v-if="isVisible"
        class="image-gallery-overlay"
        @click.self="closeModal"
        @keydown.esc="closeModal"
        tabindex="0"
      >
        <div class="image-gallery-container">
          <!-- Close Button -->
          <div class="gallery-header">
            <button
              class="gallery-close"
              @click="closeModal"
              aria-label="Close"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="#3B4648"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>
          <!-- Main Content -->
          <div class="gallery-content">
            <!-- Main Image Viewer -->
            <div class="main-image-section">
              <!-- Previous Button -->
              <button
                class="nav-button nav-prev"
                @click="previousImage"
                :disabled="currentImageIndex === 0"
                aria-label="Previous image"
              >
                <img src="/images/icon/arrow-left-circle.svg" alt="Previous" />
              </button>

              <!-- Main Image -->
              <div class="main-image-container">
                <transition name="image-slide" mode="out-in">
                  <img
                    :key="currentImageIndex"
                    :src="currentImage"
                    :alt="`Activity image ${currentImageIndex + 1}`"
                    class="main-image"
                    @load="onImageLoad"
                    @error="onImageError"
                  />
                </transition>

                <!-- Loading State -->
                <div v-if="imageLoading" class="image-loading">
                  <div class="image-spinner"></div>
                </div>
              </div>

              <!-- Next Button -->
              <button
                class="nav-button nav-next"
                @click="nextImage"
                :disabled="currentImageIndex === images.length - 1"
                aria-label="Next image"
              >
                <img src="/images/icon/arrow-right-circle.svg" alt="Next" />
              </button>
            </div>

            <!-- Thumbnail Grid -->
            <div class="thumbnail-grid">
              <button
                v-for="(image, index) in images"
                :key="`thumb-${index}`"
                class="thumbnail-item"
                :class="{ active: currentImageIndex === index }"
                @click="selectImage(index)"
              >
                <img
                  :src="image"
                  :alt="`Thumbnail ${index + 1}`"
                  class="thumbnail-image"
                />
              </button>
            </div>
          </div>

          <!-- Image Counter -->
          <div class="image-notes">
            Note: Images of student projects may show materials that are not
            provided in the Makercart
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import {
  computed,
  defineEmits,
  defineExpose,
  defineProps,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from "vue";

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  images: {
    type: Array,
    default: () => [],
  },
  initialIndex: {
    type: Number,
    default: 0,
  },
  infoText: {
    type: String,
    default:
      "Images of student projects may show materials that are not provided in the Makercart",
  },
  infoIcon: {
    type: String,
    default: "/images/icon/info-icon.svg",
  },
  infoIconAlt: {
    type: String,
    default: "Information",
  },
});

const emit = defineEmits(["close"]);

// Reactive state
const isVisible = ref(false);
const currentImageIndex = ref(0);
const imageLoading = ref(false);

// Computed
const currentImage = computed(() => {
  return props.images[currentImageIndex.value] || "";
});

// Methods
const closeModal = () => {
  isVisible.value = false;
  emit("close");
};

const openModal = (index = 0) => {
  currentImageIndex.value = Math.max(
    0,
    Math.min(index, props.images.length - 1)
  );
  isVisible.value = true;
  imageLoading.value = false;

  // Focus the modal for keyboard navigation
  nextTick(() => {
    const modal = document.querySelector(".image-gallery-overlay");
    if (modal) {
      modal.focus();
    }
  });
};

const previousImage = () => {
  if (currentImageIndex.value > 0) {
    imageLoading.value = true;
    currentImageIndex.value--;
  }
};

const nextImage = () => {
  if (currentImageIndex.value < props.images.length - 1) {
    imageLoading.value = true;
    currentImageIndex.value++;
  }
};

const selectImage = (index) => {
  if (index !== currentImageIndex.value) {
    imageLoading.value = true;
    currentImageIndex.value = index;
  }
};

const onImageLoad = () => {
  imageLoading.value = false;
};

const onImageError = () => {
  imageLoading.value = false;
  console.warn(`Failed to load image: ${currentImage.value}`);
};

// Keyboard navigation
const handleKeydown = (event) => {
  if (!isVisible.value) return;

  switch (event.key) {
    case "ArrowLeft":
      event.preventDefault();
      previousImage();
      break;
    case "ArrowRight":
      event.preventDefault();
      nextImage();
      break;
    case "Escape":
      event.preventDefault();
      closeModal();
      break;
  }
};

// Transition handlers
const onEnter = () => {
  document.body.style.overflow = "hidden";
};

const onAfterEnter = () => {
  // Animation complete
};

const onLeave = () => {
  document.body.style.overflow = "";
};

const onAfterLeave = () => {
  // Cleanup
};

// Watchers
watch(
  () => props.visible,
  (newVal) => {
    if (newVal) {
      openModal(props.initialIndex);
    } else {
      isVisible.value = false;
    }
  }
);

watch(
  () => props.initialIndex,
  (newIndex) => {
    if (props.visible) {
      currentImageIndex.value = Math.max(
        0,
        Math.min(newIndex, props.images.length - 1)
      );
    }
  }
);

// Lifecycle
onMounted(() => {
  document.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
  document.body.style.overflow = "";
});

// Expose methods for parent component
defineExpose({
  openModal,
  closeModal,
});
</script>

<style scoped>
/* Modal Overlay */
.image-gallery-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(8px);
  z-index: 999999;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  outline: none;
}

.image-gallery-container {
  background: white;
  border-radius: 16px;
  box-shadow:
    0px 10px 25px 0px rgba(0, 0, 0, 0.15),
    0px 4px 10px 0px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 1200px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
  padding: 32px 40px 40px 40px;
}

/* Header */
.gallery-header {
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  justify-content: flex-end;
  padding: 0;
}

.gallery-close {
  cursor: pointer;
  padding: 8px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    transform 0.2s ease,
    background-color 0.2s ease;
  border-radius: 8px;
}

.gallery-close:hover {
  transform: scale(1.1);
}

.gallery-close svg {
  width: 24px;
  height: 24px;
}

/* Main Content */
.gallery-content {
  display: flex;
  gap: 32px;
  flex: 1;
  align-items: flex-start;
  margin-top: 30px;
  width: 100%;
}

/* Main Image Section */
.main-image-section {
  display: flex;
  align-items: center;
  gap: 32px;
  flex-shrink: 0;
}

.nav-button {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.nav-button img {
  width: 34px;
  height: 34px;
}

.nav-button:hover:not(:disabled) {
  transform: scale(1.05);
}

.nav-button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.nav-button:disabled img {
  opacity: 0.6;
}

.main-image-container {
  position: relative;
  width: 300px;
  height: 300px;
  border-radius: 4px;
  overflow: hidden;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.main-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
}

.image-loading {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.gallery-info-section {
  margin-top: 60px;
}

.image-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f0f0f0;
  border-top: 3px solid var(--primary-blue);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Thumbnail Grid */
.thumbnail-grid {
  display: grid;
  grid-template-columns: repeat(4, 100px);
  grid-template-rows: repeat(2, 100px);
  gap: 12px;
  flex-shrink: 0;
}

.thumbnail-item {
  width: 100px;
  height: 100px;
  border-radius: 4px;
  overflow: hidden;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #f5f5f5;
  padding: 0;
}

.thumbnail-item:hover {
  transform: scale(1.02);
  border-color: #d0d4dc;
}

.thumbnail-item.active {
  border-color: var(--primary-blue);
  border-width: 4px;
}

.thumbnail-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* Image Counter */
.image-notes {
  color: var(--neutral-700);
  font-size: var(--fs-medium);
  font-weight: 400;
  display: flex;
  width: 100%;
  justify-content: flex-end;
  margin-top: 10px;
}

/* Animations */
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Modal Transitions */
.gallery-fade-enter-active,
.gallery-fade-leave-active {
  transition:
    opacity 0.18s ease-out,
    transform 0.18s ease-out;
}

.gallery-fade-enter-from,
.gallery-fade-leave-to {
  opacity: 0;
  transform: scale(0.98);
}

/* Image Slide Transitions */
.image-slide-enter-active,
.image-slide-leave-active {
  transition: all 0.3s ease;
}

.image-slide-enter-from {
  opacity: 0;
  transform: translateX(20px);
}

.image-slide-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

/* Responsive Design */
@media (max-width: 1200px) {
  .gallery-content {
    flex-direction: column;
    gap: 24px;
    align-items: center;
  }

  .main-image-section {
    gap: 24px;
  }

  .thumbnail-grid {
    grid-template-columns: repeat(6, 80px);
    grid-template-rows: 80px;
    gap: 8px;
  }

  .thumbnail-item {
    width: 80px;
    height: 80px;
  }
}

@media (max-width: 768px) {
  .image-gallery-overlay {
    padding: 20px;
  }

  .gallery-header {
    padding: 20px 20px 0 20px;
  }

  .gallery-content {
    padding: 20px;
    gap: 20px;
  }

  .main-image-container {
    width: 250px;
    height: 250px;
  }

  .main-image-section {
    gap: 16px;
  }

  .nav-button {
    width: 36px;
    height: 36px;
  }

  .thumbnail-grid {
    grid-template-columns: repeat(4, 70px);
    grid-template-rows: repeat(2, 70px);
    gap: 6px;
  }

  .thumbnail-item {
    width: 70px;
    height: 70px;
  }
}

@media (max-width: 480px) {
  .main-image-container {
    width: 200px;
    height: 200px;
  }

  .thumbnail-grid {
    grid-template-columns: repeat(3, 60px);
    grid-template-rows: repeat(2, 60px);
    gap: 4px;
  }

  .thumbnail-item {
    width: 60px;
    height: 60px;
  }
}
</style>
